import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import ThemeToggleButton from '../components/ThemeToggleButton';
import { createApiUrl } from '../config/api';

function Login() {
  const { currentUser, setCurrentUser, setAuthProvider } = useAuth();
  const navigate = useNavigate();
  
  const [loginMode, setLoginMode] = useState('email'); // 'email' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shareableLink, setShareableLink] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showShareLink, setShowShareLink] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    if (currentUser) {
      navigate('/');
    }
    
    // Generate a shareable link when component mounts
    setShareableLink('https://askharvy.com');
  }, [currentUser, navigate]);

  // Removed Google OAuth for simplified, reliable authentication

  const validatePassword = (pass) => {
    const errors = [];

    if (pass.length < 10) {
      errors.push('Minimum 10 characters required');
    }

    if (!/[A-Z]/.test(pass)) {
      errors.push('At least one uppercase letter required');
    }

    if (!/[a-z]/.test(pass)) {
      errors.push('At least one lowercase letter required');
    }

    if (!/\d/.test(pass)) {
      errors.push('At least one number required');
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass)) {
      errors.push('At least one special character required');
    }

    return errors;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    if (loginMode === 'register') {
      const errors = validatePassword(newPassword);
      setPasswordErrors(errors);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (!email || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    // Additional validation for registration
    if (loginMode === 'register') {
      const passErrors = validatePassword(password);
      if (passErrors.length > 0) {
        setError('Password does not meet security requirements');
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }
    }
    
    try {
      const endpoint = loginMode === 'register' ? 'auth/register' : 'auth/login';
      
      const requestData = {
        email: email,
        password: password,
      };
      
      if (loginMode === 'register') {
        requestData.firstName = firstName;
        requestData.lastName = lastName;
      }
      
      const response = await axios.post(createApiUrl(endpoint), requestData);
      
      if (response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        // Update auth context with user information
        if (setCurrentUser && setAuthProvider) {
          setCurrentUser(response.data.user);
          setAuthProvider('email');
        }
        
        toast.success(loginMode === 'register' ? 'Account created successfully!' : 'Successfully signed in!');
        
        // Navigate to profile page
        navigate('/profile');
      } else {
        throw new Error('No token returned from server');
      }
    } catch (err) {
      console.error('Email auth failed:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Authentication failed';
      setError(errorMessage);
      toast.error(loginMode === 'register' ? 'Registration failed: ' + errorMessage : 'Login failed: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareableLink);
    toast.success('Link copied to clipboard!');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(createApiUrl('auth/forgot-password'), {
        email: email
      });

      setResetEmailSent(true);
      toast.success('Password reset instructions sent to your email');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to send reset email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-100 font-sans antialiased flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Theme toggle in top right - Disabled */}
      {/* <div className="absolute top-6 right-6">
        <ThemeToggleButton />
      </div> */}
      
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 bg-gradient-to-br from-accent-primary to-accent-tertiary rounded-xl flex items-center justify-center shadow-lg">
            <div className="text-white font-bold text-3xl tracking-wider">H</div>
          </div>
        </div>
        <h1 className="text-3xl font-light text-dark-800 mb-4 tracking-tight leading-tight text-center">
          Meet <span className="font-medium text-accent-primary">Harvy</span>
        </h1>
        <p className="text-base text-dark-600 leading-relaxed font-light text-center max-w-sm mx-auto mb-6">
          Your all-knowing academic advisor.
        </p>
      </div>


      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="bg-dark-200/95 backdrop-blur-xl border border-dark-400/60 rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-dark-300/50 rounded-lg border border-dark-400/60 backdrop-blur-sm">
                <div className="w-1.5 h-1.5 bg-accent-primary rounded-full"></div>
                <span className="text-xs font-medium text-dark-700 tracking-tight">
                  {loginMode === 'register' ? 'Create Account' : 'Sign In'}
                </span>
              </div>
            </div>

            {/* Registration/Login Form */}
            <form onSubmit={handleEmailAuth} className="space-y-4">
              {loginMode === 'register' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-dark-700 mb-3 tracking-tight">
                        First Name
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        autoComplete="given-name"
                        required
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="block w-full bg-dark-300/80 border-2 border-dark-400/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 focus:ring-offset-dark-200 focus:border-accent-primary/40 text-sm transition-all duration-200 text-dark-700 tracking-tight leading-relaxed backdrop-blur-sm placeholder-dark-600"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-dark-700 mb-3 tracking-tight">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        autoComplete="family-name"
                        required
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="block w-full bg-dark-300/80 border-2 border-dark-400/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 focus:ring-offset-dark-200 focus:border-accent-primary/40 text-sm transition-all duration-200 text-dark-700 tracking-tight leading-relaxed backdrop-blur-sm placeholder-dark-600"
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-dark-700 mb-3 tracking-tight">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full bg-white/80 dark:bg-slate-900/80 border-2 border-slate-200/60 dark:border-slate-700/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 dark:focus:ring-slate-100/20 dark:focus:ring-offset-slate-900 focus:border-slate-900/40 dark:focus:border-slate-100/40 text-sm transition-all duration-200 text-slate-900 dark:text-slate-100 tracking-tight leading-relaxed backdrop-blur-sm placeholder-slate-400 dark:placeholder-slate-500"
                  placeholder={loginMode === 'register' ? 'Enter your Harvard email' : 'Enter your email'}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-dark-700 mb-3 tracking-tight">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={loginMode === 'register' ? 'new-password' : 'current-password'}
                  required
                  value={password}
                  onChange={handlePasswordChange}
                  className="block w-full bg-white/80 dark:bg-slate-900/80 border-2 border-slate-200/60 dark:border-slate-700/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 dark:focus:ring-slate-100/20 dark:focus:ring-offset-slate-900 focus:border-slate-900/40 dark:focus:border-slate-100/40 text-sm transition-all duration-200 text-slate-900 dark:text-slate-100 tracking-tight leading-relaxed backdrop-blur-sm placeholder-slate-400 dark:placeholder-slate-500"
                  placeholder={loginMode === 'register' ? 'Create a secure password' : 'Enter your password'}
                />
              </div>

              {/* Confirm Password Field for Registration */}
              {loginMode === 'register' && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-dark-700 mb-3 tracking-tight">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full bg-white/80 dark:bg-slate-900/80 border-2 border-slate-200/60 dark:border-slate-700/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 dark:focus:ring-slate-100/20 dark:focus:ring-offset-slate-900 focus:border-slate-900/40 dark:focus:border-slate-100/40 text-sm transition-all duration-200 text-slate-900 dark:text-slate-100 tracking-tight leading-relaxed backdrop-blur-sm placeholder-slate-400 dark:placeholder-slate-500"
                    placeholder="Confirm your password"
                  />
                </div>
              )}

              {/* Password Requirements Display for Registration */}
              {loginMode === 'register' && (
                <div className="bg-dark-300/50 border border-dark-400/60 rounded-xl p-4 backdrop-blur-sm">
                  <h4 className="text-sm font-medium text-dark-700 mb-3 tracking-tight">Security Requirements</h4>
                  <div className="space-y-2">
                    {[
                      { rule: 'Minimum 10 characters', test: password.length >= 10 },
                      { rule: 'At least one uppercase letter', test: /[A-Z]/.test(password) },
                      { rule: 'At least one lowercase letter', test: /[a-z]/.test(password) },
                      { rule: 'At least one number', test: /\d/.test(password) },
                      { rule: 'At least one special character', test: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) }
                    ].map((req, index) => (
                      <div key={index} className="flex items-center justify-between py-1">
                        <span className="text-xs text-dark-600 tracking-tight">
                          {req.rule}
                        </span>
                        <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center ${
                          req.test
                            ? 'border-green-600 bg-green-600'
                            : 'border-dark-500 bg-transparent'
                        }`}>
                          {req.test && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="rounded-xl bg-red-900/20 border border-red-800/60 p-4 backdrop-blur-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-8 h-8 bg-red-900/40 rounded-xl flex items-center justify-center">
                        <svg className="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-200 leading-relaxed">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center items-center px-8 py-4 bg-accent-primary hover:bg-accent-tertiary text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-xl shadow-sm tracking-tight leading-relaxed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {loginMode === 'register' ? 'Creating Account...' : 'Signing In...'}
                  </>
                ) : (loginMode === 'register' ? 'Create Account' : 'Sign In to Harvy')}
              </button>
              {/* Toggle between login and register */}
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setLoginMode(loginMode === 'register' ? 'email' : 'register')}
                  className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 font-medium transition-colors duration-200 tracking-tight"
                >
                  {loginMode === 'register' 
                    ? 'Already have an account? Sign in' 
                    : "Don't have an account? Sign up"}
                </button>
              </div>
              
              {/* Forgot Password Button - Only show in login mode - Separate line */}
              {loginMode === 'email' && (
                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading || !email}
                    className="text-sm text-accent-primary hover:text-accent-secondary font-medium transition-colors duration-200 tracking-tight disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resetEmailSent ? 'Reset email sent!' : 'Forgot password?'}
                  </button>
                </div>
              )}
            </form>
          </div>

          <div className="bg-slate-50/60 dark:bg-slate-900/40 px-8 py-6 border-t border-slate-200/60 dark:border-slate-700/60 backdrop-blur-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center">
                  <svg className="h-4 w-4 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Sign in to access personalized academic recommendations based on your course history and academic profile.
                </p>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200/40 dark:border-slate-700/40">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowShareLink(!showShareLink)}
                  className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 font-medium transition-colors duration-200 tracking-tight"
                >
                  {showShareLink ? "Hide sharing link" : "Share Harvy"}
                </button>
                <button
                  type="button"
                  onClick={() => window.open('/Meet_Harvy.pdf', '_blank')}
                  className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 font-medium transition-colors duration-200 tracking-tight"
                >
                  Demo
                </button>
                <button
                  type="button"
                  onClick={() => window.open('mailto:shreshth@trynoumenon.com?subject=Harvy%20Feedback')}
                  className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 font-medium transition-colors duration-200 tracking-tight"
                >
                  Review
                </button>
              </div>
              
              {showShareLink && (
                <div className="mt-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={shareableLink}
                      className="flex-1 bg-white/80 dark:bg-slate-900/80 border-2 border-slate-200/60 dark:border-slate-700/60 rounded-xl py-2 px-3 text-sm text-slate-900 dark:text-slate-100 backdrop-blur-sm tracking-tight"
                    />
                    <button
                      type="button"
                      onClick={handleCopyShareLink}
                      className="p-2.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-900/20 dark:focus:ring-slate-100/20"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    Share this link with others to let them try Harvy
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toggleable Disclaimer Info Icon - Bottom Left */}
      <div className="fixed bottom-4 left-4">
        <button
          onClick={() => setShowDisclaimer(!showDisclaimer)}
          className="w-10 h-10 bg-dark-200/95 backdrop-blur-xl border border-dark-400/60 rounded-full shadow-lg flex items-center justify-center hover:bg-dark-300/95 transition-all duration-200"
        >
          <svg className="w-5 h-5 text-dark-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>

        {showDisclaimer && (
          <div className="absolute bottom-12 left-0 max-w-md">
            <div className="bg-dark-200/95 backdrop-blur-xl border border-dark-400/60 rounded-xl shadow-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-6 h-6 bg-yellow-900/40 rounded-lg flex items-center justify-center">
                    <span className="text-yellow-400 font-bold text-xs">!</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-medium text-dark-700 mb-2 tracking-tight">Important Disclaimer</h4>
                  <p className="text-xs text-dark-600 leading-relaxed mb-3">
                    Always confirm all results with your academic advisor and verify with academic advising reports on my.harvard.
                    This was developed by students for students. You should still use my.harvard and official channels for all academic decisions.
                  </p>

                  <h4 className="text-xs font-medium text-dark-700 mb-2 tracking-tight">Privacy & Data Use</h4>
                  <p className="text-xs text-dark-600 leading-relaxed">
                    We collect only course codes and academic preferences to provide personalized recommendations.
                    No transcripts, grades, or personally identifiable academic records are stored.
                    All data is encrypted and used solely to improve your academic planning experience.
                    We do not share your information with third parties.
                  </p>

                  <div className="mt-3 pt-2 border-t border-dark-400/40">
                    <p className="text-xs text-dark-500 italic">
                      For students, by students. Use responsibly alongside official academic resources.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;